/* Mobile size guides preserve source labels, measurements and their wording. */
(() => {
  'use strict';
  const controllerKey = Symbol.for('cellini.sizeGuide.initialized');
  if (window[controllerKey]) return;
  window[controllerKey] = true;

  const adapted = new WeakSet();
  const selector = '.cellini-size-guide__scroll table';
  const element = (tag, text) => {
    const node = document.createElement(tag);
    if (text !== undefined) node.textContent = text;
    return node;
  };

  function adapt(table) {
    if (!table.isConnected || adapted.has(table) || table.classList.contains('cellini-size-guide__mobile')) return;
    if (table.querySelector('table') || table.parentElement.closest('table')) return;

    // table.rows works with both explicit thead and legacy tbody-only markup.
    const rows = [...table.rows].map((row) => [...row.cells]);
    const columns = rows[0]?.length || 0;
    if (rows.length < 2 || columns < 2 || rows.some((row) => row.length !== columns)) return;
    if (rows.some((row) => row.some((cell) => cell.colSpan !== 1 || cell.rowSpan !== 1 || !cell.textContent.trim()))) return;

    const source = rows.map((row) => row.map((cell) => cell.textContent));
    const labels = source[0];
    const measurements = source.slice(1);
    const caption = table.caption?.textContent;
    let mobile;

    if (measurements.length <= 4) {
      mobile = element('table');
      mobile.className = 'cellini-size-guide__mobile cellini-size-guide__mobile-table';
      if (measurements.length === 4) mobile.classList.add('cellini-size-guide__mobile-table--dense');
      if (caption !== undefined) mobile.append(element('caption', caption));
      const head = element('thead');
      const heading = element('tr');
      for (const label of [labels[0], ...measurements.map((row) => row[0])]) {
        const cell = element('th', label);
        cell.scope = 'col';
        heading.append(cell);
      }
      head.append(heading);
      const body = element('tbody');
      for (let column = 1; column < columns; column += 1) {
        const row = element('tr');
        const size = element('th', labels[column]);
        size.scope = 'row';
        row.append(size);
        for (const measurement of measurements) row.append(element('td', measurement[column]));
        body.append(row);
      }
      mobile.append(head, body);
    } else {
      mobile = element('div');
      mobile.className = 'cellini-size-guide__mobile cellini-size-guide__mobile-list';
      if (caption !== undefined) {
        const title = element('p', caption);
        title.className = 'cellini-size-guide__caption';
        mobile.append(title);
      }
      for (let column = 1; column < columns; column += 1) {
        const section = element('section');
        section.className = 'cellini-size-guide__size';
        section.append(element('h3', `${labels[0]} ${labels[column]}`));
        const definitions = element('dl');
        for (const measurement of measurements) {
          const pair = element('div');
          pair.append(element('dt', measurement[0]), element('dd', measurement[column]));
          definitions.append(pair);
        }
        section.append(definitions);
        mobile.append(section);
      }
    }

    // English charts remain legible within an Arabic page. Other source
    // directions are inherited from the region or copied from the table.
    const english = labels[0].trim().toLowerCase() === 'size';
    const direction = english ? 'ltr' : table.getAttribute('dir');
    if (direction) mobile.setAttribute('dir', direction);
    table.after(mobile);
    adapted.add(table);
    table.classList.add('cellini-size-guide__desktop');
    if (english) table.setAttribute('dir', 'ltr');
  }

  function scan(root) {
    if (!(root instanceof Element || root instanceof Document)) return;
    if (root instanceof Element) {
      const containingTable = root.closest(selector);
      if (containingTable) adapt(containingTable);
    }
    root.querySelectorAll(selector).forEach(adapt);
  }

  function initialize() {
    scan(document);
    document.addEventListener('shopify:section:load', (event) => scan(event.target));
    const observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) scan(node);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
